import { errorView, type ErrorView } from '@krp/client-runtime'

/**
 * Состояние пишущей команды глазами экрана. Разные исходы — разные состояния,
 * а не один красный текст (F06):
 *
 * - `sending` — запрос в пути;
 * - `unknown` — ответа нет или служба сама не знает итога: выполнено ли — неизвестно.
 *   Автоповтора НЕТ; человек нажимает «Проверить исход», и уходит ТА ЖЕ команда с
 *   тем же ключом — служба отдаёт прежний итог или выясняет его чтением;
 * - `conflict` — данные в 1С изменились (409 ревизии или чужая правка карточки):
 *   показать серверную версию, решает человек;
 * - `failed` — определённый отказ (текст 1С дословно); `resendable` — отказ до
 *   отправки в 1С, ту же команду можно отправить ещё раз тем же ключом;
 * - `succeeded` — подтверждено ответом службы.
 */
export type CommandStatus = 'sending' | 'unknown' | 'conflict' | 'failed' | 'succeeded'

export interface CommandFailure {
  status: Exclude<CommandStatus, 'sending' | 'succeeded'>
  resendable: boolean
  error: ErrorView
}

/**
 * Коды службы 1С, означающие «исход неизвестен» (B2a `outcome-unknown`, B2b `operation-unknown`); `refresh-in-progress`
 * (B5, `Catalog_TriggerSync`) — обновление каталога ещё идёт, итог служба отдаст на ту же команду тем же ключом.
 */
const UNKNOWN_CODES = new Set(['outcome-unknown', 'operation-unknown', 'operation-in-progress', 'refresh-in-progress'])

/** Ревизия конфигурации ресурса сменилась с начала правки. */
export const CONFIGURATION_MISMATCH = 'configuration-version-mismatch'

/**
 * Отказы службы, про которые она сама говорит «до записи в 1С не дошло»: та же
 * команда тем же ключом может пройти. Всё остальное 5xx на команде (502/504
 * Gateway, 500, `photo-storage-unavailable` — запись в 1С к нему уже
 * зафиксирована) — исход НЕИЗВЕСТЕН, а не «не выполнено».
 */
const NOT_SENT_CODES = new Set(['onec-unavailable', 'media-unavailable'])
/** Определённый отказ без записи: повтор не поможет. */
const REFUSED_UNAVAILABLE_CODES = new Set(['onec-unauthorized'])

export function classifyCommandError(raw: unknown): CommandFailure {
  const error = errorView(raw)
  if (error.kind === 'unknown-outcome' || UNKNOWN_CODES.has(error.code)) {
    return { status: 'unknown', resendable: true, error }
  }
  if (error.code === CONFIGURATION_MISMATCH) return { status: 'conflict', resendable: false, error }
  // 4xx без кода службы (413 от прокси, 405, 415): отказ принят на входе, до службы запрос не дошёл — исход НЕ неизвестен.
  if (error.kind === 'http' && error.status !== undefined && error.status >= 400 && error.status < 500) {
    return { status: 'failed', resendable: false, error }
  }
  if (error.kind === 'unavailable' || error.kind === 'http') {
    if (NOT_SENT_CODES.has(error.code)) return { status: 'failed', resendable: true, error }
    if (REFUSED_UNAVAILABLE_CODES.has(error.code)) return { status: 'failed', resendable: false, error }
    return { status: 'unknown', resendable: true, error }
  }
  return { status: 'failed', resendable: false, error }
}
